/**
 * Work item API module.
 *
 * Three endpoints:
 *   /items       — filter/sort/paginate (status, type, priority, tag, orderBy, order)
 *   /search?q=   — full-text search (separate endpoint on the server;
 *                  search and filter do NOT compose — pick one or the other)
 *   /stats       — cross-tenant aggregate counts (total, by status/type/priority)
 */

import { apiFetch, apiFetchWithMeta } from './client.js'
import type { WorkItem, WorkItemDetail } from '../types/api.js'

export type WorkOrderBy = 'updated_at' | 'created_at'
export type WorkOrderDir = 'ASC' | 'DESC'

export interface WorkListParams {
  status?: string
  type?: string
  priority?: string
  tag?: string | string[]
  /** An assignee name, or the sentinel `@me` (resolved to the caller server-side). */
  assignee?: string
  order_by?: WorkOrderBy
  order?: WorkOrderDir
  page?: number
  page_size?: number
}

export interface WorkListMeta {
  page?: number
  page_size?: number
  total?: number
  has_more?: boolean
}

export interface WorkStats {
  total: number
  open: number
  in_progress: number
  done: number
  cancelled: number
  ready: number
  byType: Record<string, number>
  byPriority: Record<string, number>
}

/** Fetch paginated work items list with filters/sort. */
export const fetchWorkItems = (
  params?: WorkListParams,
): Promise<{ data: WorkItem[]; meta?: WorkListMeta }> => {
  // apiFetchWithMeta flattens its params record into query string. `tag`
  // can be either a single string or a list — for the list case we join
  // with commas, which the server's `normaliseTagList` parses back.
  const flat: Record<string, string | number | boolean | undefined> = {}
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) {
        continue
      }
      flat[k] = Array.isArray(v) ? v.join(',') : v
    }
  }
  return apiFetchWithMeta<WorkItem[]>('/api/v2/work/items', flat)
}

/** Full-text search — separate endpoint, does not accept the full filter set. */
export const searchWorkItems = (
  query: string,
  params?: { type?: string; page?: number; page_size?: number },
): Promise<{ data: WorkItem[]; meta?: WorkListMeta }> =>
  apiFetchWithMeta<WorkItem[]>('/api/v2/work/search', { q: query, ...params })

/** Aggregate counts across the whole tenant — drives filter-button counts. */
export const fetchWorkStats = (): Promise<WorkStats> => apiFetch<WorkStats>('/api/v2/work/stats')

/** The distinct assignees on this project's work items — the People filter source. */
export const fetchWorkAssignees = (): Promise<{ assignees: string[] }> =>
  apiFetch<{ assignees: string[] }>('/api/v2/work/principals')

/** Fetch a single work item with full hierarchy and relationships. */
export const fetchWorkItemDetail = (itemId: string): Promise<WorkItemDetail> =>
  apiFetch<WorkItemDetail>(`/api/v2/work/items/${encodeURIComponent(itemId)}`)
